import {action, computed, makeObservable, observable, reaction} from 'mobx'
import {inject, injectable} from 'inversify'
import {subscribe} from 'dna-react-ioc'

import {ROUTES} from '@/router/routes'
import {
    fetchSelfOnboardingProcessStatus,
    submitAdditionalInfo,
    uploadOnboardingFiles,
    resendContractSigningEmail,
    fetchMsaPreview,
    submitReviewAndSign
} from './services'
import {OnboardingProcessStatusResponse, ReviewAndSignInfo} from '@/stores/onboarding/models/onboarding'
import {RouterStore, RouterStoreSymbol} from '@/router/RouterStore'
import {ProfileStoreInterface, ProfileStoreSymbol} from '@/pages/Profile'
import {IApplicationProgressStore} from '@/pages/onboarding/ApplicationProgress/IApplicationProgressStore'
import {getGlobalLoaderStore} from '@/stores/global-loader'
import {onboardingMessage} from '@/pages/onboarding/utils/onboardingNotifications'
import i18next from '@/i18n'
import {captureSentryError, getErrorMessage} from '@/services/sentry-helper'
import {
    ApplicationProgressStep,
    OnboardingProcessStatusEnum
} from '~/code/pages/onboarding/ApplicationProgress/components/ApplicationProgress/types'

const SIGN_SUCCESS_MESSAGE_DURATION_MS = 12000
const FAST_POLL_INTERVAL_MS = 1000
const FAST_POLL_MAX_ATTEMPTS = 10
const SLOW_POLL_INTERVAL_MS = 5000
const SLOW_POLL_MAX_ATTEMPTS = 12

@injectable()
export class ApplicationProgressStore implements IApplicationProgressStore {
    isLoading = false
    currentStep = 0
    isInitialized = false
    isAdditionalInfoModalOpen = false
    isResendingEmail = false
    isReviewAndSignModalOpen = false
    isPreviewingMsa = false
    isSigningContract = false
    reviewAndSignInfo: ReviewAndSignInfo | undefined = undefined

    additionalInfoModalStage: 'form' | 'confirm' | 'success' = 'form'
    additionalInfoUploadedFiles: {[key: string]: any[]} = {}
    additionalInfoSubmitting = false
    additionalInfoFormData: any = null
    additionalInfoShowCommentFields: {[key: string]: boolean} = {}
    additionalInfoUploading: {[key: string]: boolean} = {}
    additionalInfoValidationTriggered = false
    additionalInfoExpandedComments: {[key: string]: boolean} = {}
    additionalInfoForm: any = null

    additionalInfo:
        | {
              revertReasons: Array<{
                  id: string
                  name: string
                  key: string
                  date: string
                  category: string
                  reason: string
                  comment: string
                  responsibleTeam: string
                  answer: string
                  answerDate: string
                  files: any
              }>
          }
        | undefined = undefined

    applicationStatus = {
        applicationSubmission: '' as OnboardingProcessStatusEnum,
        contractPreparation: '' as OnboardingProcessStatusEnum,
        contractSigning: '' as OnboardingProcessStatusEnum,
        underwriting: '' as OnboardingProcessStatusEnum,
        acceptPayments: '' as OnboardingProcessStatusEnum
    }

    constructor(@inject(RouterStoreSymbol) private routerStore: RouterStore) {
        makeObservable(this, {
            applicationStatus: observable,
            isLoading: observable,
            currentStep: observable,
            isInitialized: observable,
            isAdditionalInfoModalOpen: observable,
            isResendingEmail: observable,
            isReviewAndSignModalOpen: observable,
            isPreviewingMsa: observable,
            isSigningContract: observable,
            reviewAndSignInfo: observable,
            msaBlobUrl: observable,
            additionalInfoModalStage: observable,
            additionalInfoUploadedFiles: observable,
            additionalInfoSubmitting: observable,
            additionalInfoFormData: observable,
            additionalInfoShowCommentFields: observable,
            additionalInfoUploading: observable,
            additionalInfoValidationTriggered: observable,
            additionalInfoExpandedComments: observable,
            additionalInfoForm: observable,
            additionalInfo: observable,
            initialize: action.bound,
            calculateCurrentStep: action.bound,
            refreshApplicationStatus: action.bound,
            updateApplicationStatus: action.bound,
            openAdditionalInfoModal: action.bound,
            closeAdditionalInfoModal: action.bound,
            setAdditionalInfoModalStage: action.bound,
            setAdditionalInfoUploadedFiles: action.bound,
            setAdditionalInfoSubmitting: action.bound,
            setAdditionalInfoFormData: action.bound,
            setAdditionalInfoForm: action.bound,
            setAdditionalInfoValidationTriggered: action.bound,
            toggleAdditionalInfoCommentField: action.bound,
            removeAdditionalInfoComment: action.bound,
            toggleAdditionalInfoExpandedComment: action.bound,
            setAdditionalInfoUploading: action.bound,
            addAdditionalInfoUploadedFile: action.bound,
            removeAdditionalInfoUploadedFile: action.bound,
            hasAdditionalInfoValidationError: action.bound,
            resetAdditionalInfoModalState: action.bound,
            uploadAdditionalInfoFile: action.bound,
            handleAdditionalInfoSubmit: action.bound,
            handleAdditionalInfoConfirmSubmit: action.bound,
            submitAdditionalInfo: action.bound,
            resendContractEmail: action.bound,
            openReviewAndSignModal: action.bound,
            closeReviewAndSignModal: action.bound,
            loadMsaPreview: action.bound,
            downloadMsa: action.bound,
            signContract: action.bound,
            processKey: computed,
            hasCompletedApplication: computed,
            isInContractPreparation: computed,
            hasReceivedContract: computed
        })

        reaction(
            () => this.processKey,
            (processKey, prevProcessKey) => {
                if (processKey && !prevProcessKey && !this.isInitialized) {
                    this.initialize()
                }
            },
            {fireImmediately: false}
        )
        this.initialize()
    }

    get hasCompletedApplication(): boolean {
        return (
            this.applicationStatus.applicationSubmission === OnboardingProcessStatusEnum.COMPLETED
        )
    }

    get isInContractPreparation(): boolean {
        return (
            this.applicationStatus.contractPreparation === OnboardingProcessStatusEnum.IN_PROGRESS
        )
    }

    get hasReceivedContract(): boolean {
        return (
            this.applicationStatus.contractSigning === OnboardingProcessStatusEnum.IN_PROGRESS ||
            this.applicationStatus.contractSigning === OnboardingProcessStatusEnum.COMPLETED
        )
    }

    get processKey(): string | undefined {
        const profileStore = subscribe<ProfileStoreInterface>(ProfileStoreSymbol)
        return profileStore.profileData?.publicId
    }

    async initialize(): Promise<void> {
        try {
            if (this.processKey && !this.isInitialized) {
                this.isLoading = true
                getGlobalLoaderStore().showGlobalLoader()
                try {
                    await this.fetchApplicationStatus(this.processKey)
                    this.isInitialized = true
                } catch (error) {
                    this.calculateCurrentStep()
                    this.isInitialized = true
                }
            }
        } catch (error) {
            captureSentryError({
                name: 'ApplicationProgressStore initialize Error',
                description: getErrorMessage(error, 'Failed to initialize application progress'),
                fingerprint: ['application-progress-store-initialize'],
                error
            })
        } finally {
            this.isLoading = false
            getGlobalLoaderStore().hideGlobalLoader()
        }
    }

    calculateCurrentStep(): void {
        const {acceptPayments, underwriting, contractSigning, contractPreparation} =
            this.applicationStatus

        if (acceptPayments === OnboardingProcessStatusEnum.COMPLETED) {
            this.currentStep = ApplicationProgressStep.ACCEPT_PAYMENTS
        } else if (
            underwriting === OnboardingProcessStatusEnum.IN_PROGRESS ||
            underwriting === OnboardingProcessStatusEnum.NEED_ADDITIONAL_INFO
        ) {
            this.currentStep = ApplicationProgressStep.UNDERWRITING
        } else if (underwriting === OnboardingProcessStatusEnum.COMPLETED) {
            this.currentStep = ApplicationProgressStep.ACCEPT_PAYMENTS
        } else if (
            contractSigning === OnboardingProcessStatusEnum.IN_PROGRESS ||
            contractSigning === OnboardingProcessStatusEnum.REVIEW_AND_SIGN_NEEDED
        ) {
            this.currentStep = ApplicationProgressStep.CONTRACT_SIGNING
        } else if (contractSigning === OnboardingProcessStatusEnum.COMPLETED) {
            this.currentStep = ApplicationProgressStep.UNDERWRITING
        } else if (contractPreparation === OnboardingProcessStatusEnum.IN_PROGRESS) {
            this.currentStep = ApplicationProgressStep.CONTRACT_PREPARATION
        } else if (contractPreparation === OnboardingProcessStatusEnum.COMPLETED) {
            this.currentStep = ApplicationProgressStep.CONTRACT_SIGNING
        } else if (this.hasCompletedApplication) {
            this.currentStep = ApplicationProgressStep.APPLICATION_SUBMITTED
        } else {
            this.currentStep = ApplicationProgressStep.APPLICATION_START
        }
    }

    async fetchApplicationStatus(businessKey: string): Promise<void> {
        try {
            const status = await fetchSelfOnboardingProcessStatus(businessKey)
            if (status?.result) {
                const response = status.result as OnboardingProcessStatusResponse
                if (response.activeProcessFound && response.activeProcess) {
                    this.updateApplicationStatus(response)
                }
            }
        } catch (error) {
            captureSentryError({
                name: 'ApplicationProgressStore fetchApplicationStatus Error',
                description: getErrorMessage(error, 'Failed to fetch application status'),
                fingerprint: ['application-progress-store-fetch-status'],
                error
            })
            throw error
        }
    }

    updateApplicationStatus(statusData: OnboardingProcessStatusResponse): void {
        const stages = statusData.activeProcess?.stages

        if (stages.applicationSubmission === 'in_progress') {
            this.routerStore.push(ROUTES.onboarding.companyDetails)
            return
        }

        this.applicationStatus = {
            applicationSubmission: stages.applicationSubmission,
            contractPreparation: stages.contractPreparation,
            contractSigning: stages.contractSigning,
            underwriting: stages.underwriting,
            acceptPayments: stages.acceptPayments
        }

        if (statusData?.additionalInfo) {
            this.additionalInfo = statusData?.additionalInfo
        }

        if (statusData?.reviewAndSignInfo) {
            this.reviewAndSignInfo = statusData.reviewAndSignInfo
        }

        if (statusData?.activeProcess?.message) {
            localStorage.setItem('applicationStatusMessage', statusData?.activeProcess?.message)
        }

        this.calculateCurrentStep()
    }

    async refreshApplicationStatus(silent = false): Promise<void> {
        try {
            if (this.processKey) {
                if (!silent) {
                    this.isLoading = true
                }
                try {
                    await this.fetchApplicationStatus(this.processKey)
                } catch (error) {
                    this.calculateCurrentStep()
                }
            }
        } catch (error) {
        } finally {
            if (!silent) {
                this.isLoading = false
            }
        }
    }

    startStatusPolling = (): (() => void) => {
        let baselineSnapshot: string | null = null
        let cancelled = false
        let pendingTimeoutId: ReturnType<typeof setTimeout> | null = null

        const takeSnapshot = (): string => JSON.stringify(this.applicationStatus)

        const scheduleNext = (delayMs: number, next: () => void): void => {
            if (cancelled) return
            pendingTimeoutId = setTimeout(next, delayMs)
        }

        const runPoll = async (
            intervalMs: number,
            remainingAttempts: number,
            onPhaseEnd?: () => void
        ): Promise<void> => {
            if (cancelled) return
            await this.refreshApplicationStatus(true)
            if (cancelled) return

            if (baselineSnapshot === null) {
                baselineSnapshot = takeSnapshot()
            } else if (takeSnapshot() !== baselineSnapshot) {
                return
            }

            if (remainingAttempts > 0) {
                scheduleNext(intervalMs, () =>
                    runPoll(intervalMs, remainingAttempts - 1, onPhaseEnd)
                )
            } else if (onPhaseEnd) {
                onPhaseEnd()
            }
        }

        const startSlowPhase = (): void => {
            runPoll(SLOW_POLL_INTERVAL_MS, SLOW_POLL_MAX_ATTEMPTS - 1)
        }

        runPoll(FAST_POLL_INTERVAL_MS, FAST_POLL_MAX_ATTEMPTS - 1, startSlowPhase)

        return () => {
            cancelled = true
            if (pendingTimeoutId !== null) {
                clearTimeout(pendingTimeoutId)
                pendingTimeoutId = null
            }
        }
    }

    openAdditionalInfoModal(): void {
        this.isAdditionalInfoModalOpen = true
    }

    closeAdditionalInfoModal(): void {
        this.isAdditionalInfoModalOpen = false
        this.resetAdditionalInfoModalState()
    }

    setAdditionalInfoModalStage(stage: 'form' | 'confirm' | 'success'): void {
        this.additionalInfoModalStage = stage
    }

    setAdditionalInfoUploadedFiles(files: {[key: string]: any[]}): void {
        this.additionalInfoUploadedFiles = files
    }

    setAdditionalInfoSubmitting(submitting: boolean): void {
        this.additionalInfoSubmitting = submitting
    }

    setAdditionalInfoFormData(data: any): void {
        this.additionalInfoFormData = data
    }

    setAdditionalInfoForm(form: any): void {
        this.additionalInfoForm = form
    }

    setAdditionalInfoValidationTriggered(triggered: boolean): void {
        this.additionalInfoValidationTriggered = triggered
    }

    toggleAdditionalInfoCommentField(reasonId: string, show?: boolean): void {
        if (show !== undefined) {
            this.additionalInfoShowCommentFields[reasonId] = show
        } else {
            this.additionalInfoShowCommentFields[reasonId] =
                !this.additionalInfoShowCommentFields[reasonId]
        }
    }

    removeAdditionalInfoComment(reasonId: string): void {
        this.additionalInfoShowCommentFields[reasonId] = false
        if (this.additionalInfoForm) {
            this.additionalInfoForm.setFieldValue(['responses', reasonId, 'answer'], '')
        }
    }

    toggleAdditionalInfoExpandedComment(reasonId: string): void {
        this.additionalInfoExpandedComments[reasonId] =
            !this.additionalInfoExpandedComments[reasonId]
    }

    setAdditionalInfoUploading(reasonId: string, uploading: boolean): void {
        this.additionalInfoUploading[reasonId] = uploading
    }

    addAdditionalInfoUploadedFile(reasonId: string, files: any[]): void {
        this.additionalInfoUploadedFiles[reasonId] = [
            ...(this.additionalInfoUploadedFiles[reasonId] || []),
            ...files
        ]
    }

    removeAdditionalInfoUploadedFile(reasonId: string, fileIndex: number): void {
        if (this.additionalInfoUploadedFiles[reasonId]) {
            this.additionalInfoUploadedFiles[reasonId] = this.additionalInfoUploadedFiles[
                reasonId
            ].filter((_, index) => index !== fileIndex)
        }
        if (this.additionalInfoForm) {
            this.additionalInfoForm.setFields([
                {
                    name: ['responses', reasonId, 'files'],
                    errors: []
                }
            ])
        }
    }

    hasAdditionalInfoValidationError(reasonId: string): boolean {
        if (!this.additionalInfoValidationTriggered) return false

        const hasAnswer = this.additionalInfoForm
            ?.getFieldValue(['responses', reasonId, 'answer'])
            ?.trim()
        const hasFiles =
            this.additionalInfoUploadedFiles[reasonId] &&
            this.additionalInfoUploadedFiles[reasonId].length > 0
        return !hasAnswer && !hasFiles
    }

    resetAdditionalInfoModalState(): void {
        this.additionalInfoModalStage = 'form'
        this.additionalInfoUploadedFiles = {}
        this.additionalInfoSubmitting = false
        this.additionalInfoFormData = null
        this.additionalInfoShowCommentFields = {}
        this.additionalInfoUploading = {}
        this.additionalInfoValidationTriggered = false
        this.additionalInfoExpandedComments = {}
        this.additionalInfoForm = null
    }

    async uploadAdditionalInfoFile(
        file: any,
        reasonId: string,
        processKey: string
    ): Promise<boolean> {
        if (!processKey) {
            console.error('Process key not available')
            return false
        }

        this.setAdditionalInfoUploading(reasonId, true)

        try {
            const fileObj = file.originFileObj || file
            const result = await uploadOnboardingFiles(
                processKey,
                [fileObj],
                'revert-reason-document'
            )

            if (result.success && result.uploadedFiles) {
                const documentsWithType = result.uploadedFiles.map((uploadedFile: any) => ({
                    type: 'revert-reason-document',
                    name: uploadedFile.name || file.name,
                    path: uploadedFile.path || uploadedFile
                }))

                this.addAdditionalInfoUploadedFile(reasonId, documentsWithType)

                if (this.additionalInfoForm) {
                    this.additionalInfoForm.setFields([
                        {
                            name: ['responses', reasonId, 'files'],
                            errors: []
                        },
                        {
                            name: ['responses', reasonId, 'answer'],
                            errors: []
                        }
                    ])
                }

                return true
            } else {
                return false
            }
        } catch (error) {
            console.error('File upload failed:', error)
            return false
        } finally {
            this.setAdditionalInfoUploading(reasonId, false)
        }
    }

    async handleAdditionalInfoSubmit(): Promise<void> {
        if (!this.additionalInfoForm) return

        try {
            this.setAdditionalInfoSubmitting(true)
            this.setAdditionalInfoValidationTriggered(true)

            const formValues = this.additionalInfoForm.getFieldsValue()
            const responses = formValues.responses || {}

            try {
                await this.additionalInfoForm.validateFields()
            } catch (error) {
                console.log('Form validation failed:', error)
                return
            }

            const submissionData = {
                revertReasons:
                    this.additionalInfo?.revertReasons.map((reason) => {
                        const userAnswer =
                            responses[reason.id]?.answer ||
                            this.additionalInfoForm.getFieldValue([
                                'responses',
                                reason.id,
                                'answer'
                            ]) ||
                            ''
                        return {
                            ...reason,
                            answer: userAnswer,
                            answerDate: new Date().toISOString().slice(0, 19).replace('T', ' '),
                            files: this.additionalInfoUploadedFiles[reason.id] || []
                        }
                    }) || []
            }

            this.setAdditionalInfoFormData(submissionData)
            this.setAdditionalInfoModalStage('confirm')
        } catch (error) {
            captureSentryError({
                name: 'ApplicationProgressStore handleAdditionalInfoSubmit Error',
                description: getErrorMessage(error, 'Failed to submit additional info'),
                fingerprint: ['application-progress-store-handle-additional-info-submit'],
                error
            })
            console.error('Form submission failed:', error)
        } finally {
            this.setAdditionalInfoSubmitting(false)
        }
    }

    async handleAdditionalInfoConfirmSubmit(): Promise<void> {
        try {
            this.setAdditionalInfoSubmitting(true)

            if (!this.processKey || !this.additionalInfoFormData) {
                console.error('Process key or form data not available')
                return
            }

            await this.submitAdditionalInfo(this.processKey, this.additionalInfoFormData)
            this.setAdditionalInfoModalStage('success')
        } catch (error: any) {
            this.setAdditionalInfoModalStage('form')
        } finally {
            this.setAdditionalInfoSubmitting(false)
        }
    }

    async submitAdditionalInfo(processKey: string, submissionData: any): Promise<void> {
        try {
            await submitAdditionalInfo(processKey, submissionData)
            await this.refreshApplicationStatus()

            this.closeAdditionalInfoModal()
        } catch (error) {
            throw error
        }
    }

    msaBlobUrl: string | undefined = undefined

    openReviewAndSignModal(): void {
        this.isReviewAndSignModalOpen = true
        this.loadMsaPreview()
    }

    closeReviewAndSignModal(): void {
        this.isReviewAndSignModalOpen = false
        if (this.msaBlobUrl) {
            URL.revokeObjectURL(this.msaBlobUrl)
            this.msaBlobUrl = undefined
        }
    }

    async loadMsaPreview(): Promise<void> {
        if (!this.reviewAndSignInfo || this.msaBlobUrl) return

        this.isPreviewingMsa = true
        try {
            const blob = await fetchMsaPreview({
                name: this.reviewAndSignInfo.signatory.name,
                email: this.reviewAndSignInfo.signatory.email
            })
            this.msaBlobUrl = URL.createObjectURL(blob)
        } catch (error) {
            onboardingMessage.error(i18next.t('ReviewAndSignModal.errors.previewFailed'))
        } finally {
            this.isPreviewingMsa = false
        }
    }

    downloadMsa(): void {
        if (!this.msaBlobUrl) return
        const link = document.createElement('a')
        link.href = this.msaBlobUrl
        link.download = 'PREVIEW. DNA Payments Application Form.pdf'
        link.click()
    }

    async signContract(): Promise<void> {
        if (!this.processKey) return

        this.isSigningContract = true
        try {
            await submitReviewAndSign(this.processKey)
            this.closeReviewAndSignModal()
            onboardingMessage.success(
                i18next.t('ReviewAndSignModal.messages.signSuccess'),
                SIGN_SUCCESS_MESSAGE_DURATION_MS
            )
            await this.refreshApplicationStatus()
        } catch (error) {
            onboardingMessage.error(i18next.t('ReviewAndSignModal.errors.signFailed'))
        } finally {
            this.isSigningContract = false
        }
    }

    async resendContractEmail(): Promise<void> {
        try {
            if (!this.processKey) {
                console.error('Process key not available')
                return
            }
            this.isResendingEmail = true
            await resendContractSigningEmail(this.processKey)
            onboardingMessage.success(i18next.t('ApplicationProgress.contractEmailSentSuccess'))
        } catch (error) {
            captureSentryError({
                name: 'ApplicationProgressStore resendContractEmail Error',
                description: getErrorMessage(error, 'Failed to resend contract email'),
                fingerprint: ['application-progress-store-resend-contract-email'],
                error
            })
            console.error('Error resending contract email:', error)
            throw error
        } finally {
            this.isResendingEmail = false
        }
    }
}
